import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, FormControlLabel, IconButton, Modal, Switch, TextField, Typography } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import apiClient from "../../axiosClient";
import modalStyle from "../../styles/modalStyle";
import DropDown from "../baseComponents/dropdown";
import EntityPermissions from "../../interfaces/entityPermissions";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import Permissions from "../../interfaces/permissions";
import DropDownValue from "../baseComponents/dropdownvalue";

interface HandleEntityPermissionsModalProps {
    open: boolean;
    onClose: () => void;
    selectedEntityPermission?: EntityPermissions;
    getAPIFunction: () => Promise<void>;
    modalMode: string;
};

const entityTypes = { client: 'Client', agency: 'Agency' };
const horizontalStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center' };

const HandleEntityPermissionsModal: React.FC<HandleEntityPermissionsModalProps> = ({
    open, onClose, getAPIFunction, selectedEntityPermission, modalMode
}) => {
    const isEditMode = selectedEntityPermission && modalMode === "edit";
    const permissions = useSelector((state: RootState) => state.permissions.permissions).filter(val => val.is_active);
    const clients = useSelector((state: RootState) => state.clients.clients);
    const agencies = useSelector((state: RootState) => state.agencies.agencies);
    const [entityName, setEntityName] = useState<string>('');
    const [entityType, setEntityType] = useState<string>('');
    const [permission, setPermission] = useState<Permissions | undefined>(undefined);
    const [licenseCount, setLicenseCount] = useState<number>(0);
    const [isPermissionGranted, setIsPermissionGranted] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        if (isEditMode) {
            setEntityName(selectedEntityPermission.entity_name);
            setEntityType(selectedEntityPermission.entity_type);
            setPermission(permissions.find(val => val.permission_name === selectedEntityPermission.permission_name));
            if (selectedEntityPermission.license_count !== undefined) {
                setLicenseCount(selectedEntityPermission.license_count);
            }
            setIsPermissionGranted(selectedEntityPermission.is_permission_granted);
        }
    }, [selectedEntityPermission]);

    const createOrEditEntityPermission = async () => {
        try {
            if (!entityType || !entityName || !permission || (permission && permission.permission_type === "licensed" && licenseCount === 0)) {
                alert('Please enter all details');
                return;
            }
            setLoading(true);
            let response;
            const body = [{
                entity_permission_id: isEditMode ? selectedEntityPermission.entity_permission_id : null,
                entity_type: entityType,
                entity_name: entityName,
                permission_name: permission.permission_name,
                is_permission_granted: isPermissionGranted,
                license_count: permission.permission_type === "licensed" ? licenseCount : null,
            }];
            if (isEditMode) {
                response = await apiClient.put('/api/v1/entity_permissions', body);
            } else {
                response = await apiClient.post('/api/v1/entity_permissions', body);
            }
            if (response.status === 200) {
                if (isEditMode) {
                    alert('Entity Permission updated successfully');
                } else {
                    alert('New Entity permission created successfully');
                }
                handleClose();
                await getAPIFunction();
            }
        } catch (error: any) {
            if (error.response) {
                alert('Error response data:' + error.response.data.message);
            } else if (error.request) {
                alert('No response received:' + error.request);
            } else {
                alert('Error message:' + error.message);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setEntityName('');
        setEntityType('');
        setPermission(undefined);
        setLicenseCount(0);
        setIsPermissionGranted(true);
        onClose();
    };

    return (
        <Modal open={open} onClose={handleClose}>
            <Box sx={modalStyle}>
                {loading ? (
                    <Box sx={{ width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
                        <CircularProgress color="primary" />
                    </Box>
                ) : (
                    <>
                        <Box sx={horizontalStyle}>
                            <Typography id="modal-title" variant="h6">
                                {isEditMode ? 'Edit' : 'Add New'} Permission
                            </Typography>
                            <IconButton onClick={handleClose}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                        <Box sx={{ ...horizontalStyle, mt: 2 }}>
                            <DropDown
                                label="Select Entity Type"
                                items={Object.values(entityTypes)}
                                selectedValue={entityType}
                                onSelection={setEntityType}
                                style={{ width: entityType ? '49%' : '100%' }}
                                disabled={isEditMode}
                            />
                            {entityType && (
                                <DropDownValue
                                    label={`Select ${entityType}`}
                                    items={entityType === entityTypes.agency ? agencies : entityType === entityTypes.client ? clients : []}
                                    selectedValue={entityName}
                                    onSelection={setEntityName}
                                    displayProp="name"
                                    valueProp={entityType === entityTypes.agency ? "agency_name" : entityType === entityTypes.client ? "client_name" : ""}
                                    keyProp={entityType === entityTypes.agency ? "agency_name" : entityType === entityTypes.client ? "client_name" : ""}
                                    style={{ width: entityType ? '49%' : '100%' }}
                                    disabled={isEditMode}
                                />
                            )}
                        </Box>
                        <Box sx={{ ...horizontalStyle, mt: 2 }}>
                            <DropDownValue
                                label="Select Permission"
                                items={permissions}
                                selectedValue={permission}
                                onSelection={setPermission}
                                displayProp="permission_name"
                                keyProp="permission_name"
                                style={{ width: permission?.permission_type === "licensed" ? '49%' : '100%' }}
                                disabled={isEditMode}
                            />
                            {permission?.permission_type === "licensed" && (
                                <TextField
                                    label="Select License Count"
                                    value={licenseCount}
                                    type="number"
                                    onChange={e => setLicenseCount(parseInt(e.target.value))}
                                    style={{ width: '49%' }}
                                />
                            )}
                        </Box>
                        {isEditMode && (
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={isPermissionGranted}
                                        onChange={e => setIsPermissionGranted(e.target.checked)}
                                        name="Is Permission Granted"
                                        size="medium"
                                    />
                                }
                                sx={{ mt: 2 }}
                                label="Will this permission be granted?*"
                                labelPlacement="end"
                            />
                        )}
                        <Box mt={2} textAlign="right">
                            <Button variant="contained" color="primary" onClick={createOrEditEntityPermission}>
                                <Typography>Submit</Typography>
                            </Button>
                        </Box>
                    </>
                )}
            </Box>
        </Modal>
    );
};

export default HandleEntityPermissionsModal;